import {
  CLOTHING_SIZE_FIELDS,
  FOOTWEAR_SIZE_FIELDS,
  getSizeFieldsForProfile,
} from "../../../Routes/Marketplace/libs/productSizes";

/** Exact DB category names → form profile id */
const CATEGORY_TO_PROFILE = {
  Fashion: "fashion",
  "Phones & Tablets": "phones",
  Computing: "computing",
  Electronics: "electronics",
  Gaming: "gaming",
  Appliance: "appliance",
  Supermarket: "grocery",
  "Super Market": "grocery",
  Grocery: "grocery",
  "Drink & Groceries": "grocery",
  "Health & Beauty": "beauty",
  "Home & Office": "home",
  "Kitchen Utensils": "home",
  "Building Material": "building",
  "Building Materials": "building",
  "Baby, kids & Toy": "baby",
  "Baby Products": "baby",
  "Sporting Goods": "sporting",
};

const normalizeCategoryKey = (name) =>
  String(name || "")
    .trim()
    .replace(/\s+/g, " ");

const matchProfileId = (categoryName) => {
  const key = normalizeCategoryKey(categoryName);
  if (!key) return "general";
  if (CATEGORY_TO_PROFILE[key]) return CATEGORY_TO_PROFILE[key];

  const lower = key.toLowerCase();
  for (const [label, profileId] of Object.entries(CATEGORY_TO_PROFILE)) {
    if (label.toLowerCase() === lower) return profileId;
  }
  if (lower.includes("fashion") || lower.includes("cloth")) return "fashion";
  if (lower.includes("phone") || lower.includes("tablet")) return "phones";
  if (lower.includes("comput")) return "computing";
  if (lower.includes("electronic")) return "electronics";
  if (lower.includes("game")) return "gaming";
  if (lower.includes("appliance")) return "appliance";
  if (lower.includes("groc") || lower.includes("supermarket") || lower.includes("drink")) return "grocery";
  if (lower.includes("health") || lower.includes("beauty")) return "beauty";
  if (lower.includes("kitchen") || lower.includes("home") || lower.includes("office")) return "home";
  if (lower.includes("building")) return "building";
  if (lower.includes("baby") || lower.includes("toy") || lower.includes("kid")) return "baby";
  if (lower.includes("sport")) return "sporting";

  return "general";
};

const COLOR_OPTIONS = [
  "Black",
  "White",
  "Blue",
  "Red",
  "Green",
  "Yellow",
  "Grey",
  "Brown",
  "Pink",
  "Purple",
  "Orange",
  "Silver",
  "Gold",
  "Multi-color",
];

const UNIT_OPTIONS = ["kg", "g", "L", "ml", "pack", "piece", "carton", "dozen"];

const PROFILES = {
  fashion: {
    id: "fashion",
    title: "Fashion & apparel",
    hint: "Add brand, color, and stock per clothing size — buyers pick a size at checkout (like Jumia).",
    namePlaceholder: "e.g. Men's Slim Fit Oxford Shirt",
    descriptionPlaceholder: "Fabric, fit, care instructions, and styling notes.",
    showBrand: true,
    brandRequired: false,
    showColor: true,
    colorMode: "select",
    showSizes: true,
    sizeMode: "clothing",
    sizeHint: "Enter units in stock for each size you sell. Leave blank for sizes you don't offer.",
    attributeFields: [
      { key: "gender", label: "Gender / fit", type: "select", options: ["Men", "Women", "Unisex", "Boys", "Girls"] },
      { key: "material", label: "Material", placeholder: "e.g. Cotton, Polyester blend" },
    ],
  },
  phones: {
    id: "phones",
    title: "Phones & tablets",
    hint: "Add device specs so buyers can compare models easily.",
    namePlaceholder: "e.g. Samsung Galaxy A15 128GB",
    descriptionPlaceholder: "Condition, included accessories, network bands, and key features.",
    showBrand: true,
    brandRequired: true,
    showColor: true,
    colorMode: "text",
    colorLabel: "Color / finish",
    colorPlaceholder: "e.g. Black, Midnight Blue",
    showSizes: false,
    attributeFields: [
      { key: "model", label: "Model number", placeholder: "e.g. SM-A156B" },
      { key: "storage", label: "Storage", placeholder: "e.g. 128GB", required: true },
      { key: "ram", label: "RAM", placeholder: "e.g. 6GB" },
      { key: "screenSize", label: "Screen size", placeholder: 'e.g. 6.5"' },
      { key: "battery", label: "Battery", placeholder: "e.g. 5000mAh" },
      { key: "warranty", label: "Warranty", placeholder: "e.g. 12 months" },
    ],
  },
  computing: {
    id: "computing",
    title: "Computing",
    hint: "Laptops, desktops, monitors, and accessories.",
    namePlaceholder: "e.g. HP Pavilion 15 Laptop",
    descriptionPlaceholder: "Specs, ports, condition, and what's included in the box.",
    showBrand: true,
    brandRequired: true,
    showColor: true,
    colorMode: "text",
    colorLabel: "Color / finish",
    showSizes: false,
    attributeFields: [
      { key: "model", label: "Model", placeholder: "e.g. 15-eg2000nia" },
      { key: "processor", label: "Processor", placeholder: "e.g. Intel Core i5", required: true },
      { key: "ram", label: "RAM", placeholder: "e.g. 16GB", required: true },
      { key: "storage", label: "Storage", placeholder: "e.g. 512GB SSD", required: true },
      { key: "screenSize", label: "Screen size", placeholder: 'e.g. 15.6"' },
      { key: "os", label: "Operating system", placeholder: "e.g. Windows 11" },
      { key: "warranty", label: "Warranty", placeholder: "e.g. 1 year" },
    ],
  },
  electronics: {
    id: "electronics",
    title: "Electronics",
    hint: "TVs, audio, cameras, and general electronics.",
    namePlaceholder: "e.g. Sony 55\" Smart TV",
    descriptionPlaceholder: "Features, connectivity, condition, and package contents.",
    showBrand: true,
    brandRequired: true,
    showColor: true,
    colorMode: "text",
    colorLabel: "Color / finish",
    showSizes: false,
    attributeFields: [
      { key: "model", label: "Model number", placeholder: "e.g. KD-55X80K" },
      { key: "power", label: "Power / wattage", placeholder: "e.g. 120W" },
      { key: "voltage", label: "Voltage", placeholder: "e.g. 220V" },
      { key: "connectivity", label: "Connectivity", placeholder: "e.g. HDMI, Bluetooth, Wi‑Fi" },
      { key: "warranty", label: "Warranty", placeholder: "e.g. 24 months" },
    ],
  },
  gaming: {
    id: "gaming",
    title: "Gaming",
    hint: "Consoles, games, controllers, and gaming gear.",
    namePlaceholder: "e.g. PlayStation 5 Console",
    descriptionPlaceholder: "Platform, edition, condition, and included items.",
    showBrand: true,
    brandRequired: false,
    showColor: true,
    colorMode: "text",
    colorLabel: "Color / edition",
    showSizes: false,
    attributeFields: [
      { key: "platform", label: "Platform", placeholder: "e.g. PS5, Xbox, PC", required: true },
      { key: "genre", label: "Genre", placeholder: "e.g. Action, Sports" },
      { key: "model", label: "Model / edition", placeholder: "e.g. Disc Edition" },
      { key: "warranty", label: "Warranty", placeholder: "Optional" },
    ],
  },
  appliance: {
    id: "appliance",
    title: "Appliances",
    hint: "Home appliances — include capacity and power details.",
    namePlaceholder: "e.g. LG 350L Double Door Fridge",
    descriptionPlaceholder: "Capacity, energy rating, condition, and installation notes.",
    showBrand: true,
    brandRequired: true,
    showColor: true,
    colorMode: "text",
    colorLabel: "Color / finish",
    showSizes: false,
    attributeFields: [
      { key: "model", label: "Model number", placeholder: "e.g. GC-B569NQCM" },
      { key: "capacity", label: "Capacity", placeholder: "e.g. 350L", required: true },
      { key: "power", label: "Power rating", placeholder: "e.g. 150W" },
      { key: "energyRating", label: "Energy rating", placeholder: "e.g. A++" },
      { key: "warranty", label: "Warranty", placeholder: "e.g. 2 years" },
    ],
  },
  grocery: {
    id: "grocery",
    title: "Grocery & supermarket",
    hint: "Food and drinks — weight, unit, and expiry help buyers decide.",
    namePlaceholder: "e.g. Golden Penny Semolina 10kg",
    descriptionPlaceholder: "Ingredients, allergens, storage, and origin.",
    showBrand: true,
    brandRequired: false,
    showColor: false,
    showSizes: false,
    attributeFields: [
      { key: "netWeight", label: "Net weight / volume", placeholder: "e.g. 10", required: true },
      {
        key: "unit",
        label: "Unit",
        type: "select",
        options: UNIT_OPTIONS,
        required: true,
      },
      { key: "expiryDate", label: "Best before / expiry", type: "date" },
      { key: "packSize", label: "Pack size", placeholder: "e.g. 12 x 50cl" },
    ],
  },
  beauty: {
    id: "beauty",
    title: "Health & beauty",
    hint: "Cosmetics, skincare, and personal care products.",
    namePlaceholder: "e.g. Nivea Soft Moisturizing Cream 200ml",
    descriptionPlaceholder: "Ingredients, skin type, usage, and certifications.",
    showBrand: true,
    brandRequired: true,
    showColor: false,
    showSizes: false,
    attributeFields: [
      { key: "volume", label: "Volume / size", placeholder: "e.g. 200ml", required: true },
      { key: "skinType", label: "Skin / hair type", placeholder: "e.g. Dry skin, All types" },
      { key: "expiryDate", label: "Expiry date", type: "date" },
      { key: "ingredients", label: "Key ingredients", placeholder: "Optional summary" },
    ],
  },
  home: {
    id: "home",
    title: "Home & kitchen",
    hint: "Furniture, décor, and kitchen items — dimensions matter.",
    namePlaceholder: "e.g. 6-Seater Dining Table Set",
    descriptionPlaceholder: "Materials, assembly, care, and what's included.",
    showBrand: true,
    brandRequired: false,
    showColor: true,
    colorMode: "text",
    colorLabel: "Color / pattern",
    showSizes: false,
    attributeFields: [
      { key: "material", label: "Material", placeholder: "e.g. Solid wood, Stainless steel" },
      { key: "dimensions", label: "Dimensions (L × W × H)", placeholder: "e.g. 180 × 90 × 75 cm" },
      { key: "capacity", label: "Capacity", placeholder: "e.g. 5L, 12 pieces" },
    ],
  },
  building: {
    id: "building",
    title: "Building materials",
    hint: "Construction supplies — specify unit and dimensions.",
    namePlaceholder: "e.g. Dulux Weather Shield Paint 20L",
    descriptionPlaceholder: "Coverage, application, and storage instructions.",
    showBrand: true,
    brandRequired: false,
    showColor: true,
    colorMode: "text",
    colorLabel: "Color / finish",
    showSizes: false,
    attributeFields: [
      { key: "material", label: "Material type", placeholder: "e.g. Cement, Paint, Tiles" },
      { key: "dimensions", label: "Dimensions / coverage", placeholder: "e.g. 60×60cm, 20L" },
      {
        key: "unit",
        label: "Sold by",
        type: "select",
        options: ["piece", "bag", "kg", "carton", "bundle", "sqm", "L"],
      },
    ],
  },
  baby: {
    id: "baby",
    title: "Baby, kids & toys",
    hint: "Include age range and safety info where relevant.",
    namePlaceholder: "e.g. Fisher-Price Activity Gym",
    descriptionPlaceholder: "Age suitability, materials, and safety certifications.",
    showBrand: true,
    brandRequired: false,
    showColor: true,
    colorMode: "select",
    showSizes: false,
    attributeFields: [
      { key: "ageRange", label: "Age range", placeholder: "e.g. 0–12 months", required: true },
      { key: "material", label: "Material", placeholder: "e.g. BPA-free plastic" },
      { key: "safety", label: "Safety notes", placeholder: "e.g. CE certified" },
    ],
  },
  sporting: {
    id: "sporting",
    title: "Sporting goods",
    hint: "Shoes and gear — add EU shoe sizes and stock per size when applicable.",
    namePlaceholder: "e.g. Nike Air Zoom Running Shoes",
    descriptionPlaceholder: "Sport type, fit, and usage recommendations.",
    showBrand: true,
    brandRequired: false,
    showColor: true,
    colorMode: "text",
    colorLabel: "Color",
    showSizes: true,
    sizeMode: "footwear",
    sizeHint: "EU shoe sizes (38–43). For clothing, list under Fashion instead.",
    attributeFields: [
      { key: "sportType", label: "Sport / activity", placeholder: "e.g. Football, Gym" },
      { key: "material", label: "Material", placeholder: "e.g. Synthetic mesh" },
    ],
  },
  general: {
    id: "general",
    title: "General product",
    hint: "Add any details that help buyers understand your item.",
    namePlaceholder: "Product name",
    descriptionPlaceholder: "Describe the product clearly for buyers.",
    showBrand: true,
    brandRequired: false,
    showColor: true,
    colorMode: "text",
    colorLabel: "Color / variant",
    showSizes: false,
    attributeFields: [
      { key: "variant", label: "Variant / option", placeholder: "e.g. Large pack, Blue" },
      { key: "dimensions", label: "Size / dimensions", placeholder: "Optional" },
      { key: "material", label: "Material", placeholder: "Optional" },
    ],
  },
};

export const getCategoryFormProfile = (categoryName) => {
  const profileId = matchProfileId(categoryName);
  return PROFILES[profileId] || PROFILES.general;
};

export const getSizeFieldsForCategory = (categoryName) => {
  const profile = getCategoryFormProfile(categoryName);
  if (!profile.showSizes) return [];
  return getSizeFieldsForProfile(profile.sizeMode || "clothing");
};

export const COLOR_SELECT_OPTIONS = COLOR_OPTIONS;

const ATTRIBUTE_LABELS = Object.values(PROFILES).reduce((map, profile) => {
  profile.attributeFields.forEach((field) => {
    map[field.key] = field.label;
  });
  return map;
}, {});

const humanizeKey = (key) =>
  String(key)
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (s) => s.toUpperCase())
    .trim();

/** Normalize attributes from API (plain object or Mongoose Map) */
export const normalizeProductAttributes = (attributes) => {
  if (!attributes) return {};
  if (attributes instanceof Map) {
    return Object.fromEntries(attributes.entries());
  }
  if (typeof attributes === "object") {
    return { ...attributes };
  }
  return {};
};

/** Spec rows for customer / merchant display */
export const getProductSpecEntries = (product) => {
  if (!product) return [];
  const attrs = normalizeProductAttributes(product.attributes);
  return Object.entries(attrs)
    .filter(([, value]) => value !== undefined && value !== null && String(value).trim() !== "")
    .map(([key, value]) => ({
      key,
      label: ATTRIBUTE_LABELS[key] || humanizeKey(key),
      value: String(value),
    }));
};

export { CLOTHING_SIZE_FIELDS, FOOTWEAR_SIZE_FIELDS, PROFILES, matchProfileId };
